import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { completeTelegramPairing } from "@/lib/api/telegram-pairing.functions";
import { CheckCircle2, Loader2, Send, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/telegram/link/$token")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Link your Telegram chat | Nkadimeng Danny Legal & Compliance" },
      {
        name: "description",
        content:
          "Connect your Telegram chat to your Nkadimeng Danny Legal & Compliance account so you can book consultations and track them from Telegram.",
      },
      { property: "og:title", content: "Link your Telegram chat" },
      {
        property: "og:description",
        content:
          "Connect Telegram to your Nkadimeng Danny Legal account to book and track consultations in chat.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TelegramLinkPage,
});

function TelegramLinkPage() {
  const { token } = Route.useParams();
  const pair = useServerFn(completeTelegramPairing);
  const [state, setState] = useState<"checking" | "signin" | "working" | "done" | "error">(
    "checking",
  );
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!data.session) {
        setState("signin");
        return;
      }
      setState("working");
      try {
        await pair({ data: { token } });
        if (!cancelled) setState("done");
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? "Could not link this chat.");
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, pair]);

  return (
    <section className="container-narrow py-20">
      <div className="mx-auto max-w-lg rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
        <Send className="mx-auto h-10 w-10 text-gold" />
        <h1 className="mt-4 font-display text-2xl text-navy">Link your Telegram chat</h1>

        {state === "checking" || state === "working" ? (
          <p className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Connecting your account…
          </p>
        ) : null}

        {state === "signin" && (
          <>
            <p className="mt-4 text-sm text-muted-foreground">
              Sign in to your account and we'll connect this Telegram chat to it
              automatically.
            </p>
            <Link
              to="/auth"
              search={{ redirect: `/telegram/link/${token}` }}
              className="mt-6 inline-flex items-center justify-center rounded-md bg-navy px-6 py-2.5 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
            >
              Sign in to continue
            </Link>
          </>
        )}

        {state === "done" && (
          <>
            <p className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Your Telegram chat is linked.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Head back to Telegram — your details are now pre-filled when you book,
              and <code>/mybookings</code> shows your full history.
            </p>
          </>
        )}

        {state === "error" && (
          <p className="mt-4 flex items-center justify-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}
      </div>
    </section>
  );
}
